import { useLazyPostUserReactivateQuery } from '@/api';
import { useLazyGetDeactivatedUsersQuery, DeactivatedUserData } from '@/api/users/getDeactivatedUsers';
import {
  selectDeactivatedUsersList,
  selectDeactivatedUsersPagination,
  selectDeactivatedUsersRowCount,
  selectUsersFinding,
  setDeactivatedUsersPagination,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { UserReactivateIcon, UserAvatar } from '@repo/atomic-components';
import { OutlineIconButton } from '@/ui/components/OutlineIconButton';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { format } from 'date-fns';
import { classNames } from 'primereact/utils';
import { useCallback, useEffect, useMemo } from 'react';

import { AccessControlAction } from '@/constants';
import { useAccessControl } from '@/hooks';

export const useDeactivatedUsersListTable = () => {
  const dispatch = useAppDispatch();

  const deactivatedUsersList = useAppSelector(selectDeactivatedUsersList);
  const deactivatedUsersRowCount = useAppSelector(selectDeactivatedUsersRowCount);
  const deactivatedUsersPagination = useAppSelector(selectDeactivatedUsersPagination);

  const usersFinding = useAppSelector(selectUsersFinding);

  const isAddEditUsers = useAccessControl(AccessControlAction.ADD_EDIT_USERS);

  const [triggerDeactivatedUsers, { isFetching: getDeactivatedUsersFetching }] = useLazyGetDeactivatedUsersQuery();
  const [triggerPostUserReactivate] = useLazyPostUserReactivateQuery();

  useEffect(() => {
    triggerDeactivatedUsers(
      {
        pageIndex: deactivatedUsersPagination.pageIndex,
        pageSize: deactivatedUsersPagination.pageSize,
        name: usersFinding.length ? usersFinding : undefined,
      },
      false,
    );
  }, [
    deactivatedUsersPagination.pageIndex,
    deactivatedUsersPagination.pageSize,
    triggerDeactivatedUsers,
    usersFinding,
  ]);

  const handleRestoreUserClick = useCallback(
    (userId: string) => {
      triggerPostUserReactivate({ userToEditId: userId });
    },
    [triggerPostUserReactivate],
  );

  const columns = useMemo<ColumnDef<DeactivatedUserData, unknown>[]>(
    () => [
      {
        accessorKey: 'userName',
        header: 'Name',
        cell: (info) => {
          const userName = info.getValue() as string;
          if (!userName) return '';
          return (
            <div className="flex items-center gap-2">
              <UserAvatar name={userName} size={24} fontSize={10} />
              <span
                className={classNames('block max-w-[300px] overflow-hidden overflow-ellipsis whitespace-nowrap')}
                title={userName}
              >
                {userName}
              </span>
            </div>
          );
        },
        size: 300,
      },
      {
        accessorKey: 'userRole',
        header: 'Role',
        cell: (info) => <span>{info.getValue() as string}</span>,
        size: 120,
      },
      {
        accessorKey: 'userEmail',
        header: 'Email',
        cell: (info) => <span>{info.getValue() as string}</span>,
        size: 200,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created At',
        cell: (info) => <span>{format(new Date(info.getValue() as string), 'dd MMM yyyy')}</span>,
        size: 120,
      },
      {
        accessorKey: 'deactivatedAt',
        header: 'Deactivated At',
        cell: (info) => <span>{format(new Date(info.getValue() as string), 'dd MMM yyyy')}</span>,
        size: 120,
      },
      {
        header: '',
        id: 'actions',
        cell: (info) => {
          const rowValue = info.row.original;

          return (
            <div className="flex flex-nowrap items-center justify-end gap-2 opacity-0 transition-opacity group-hover:opacity-100">
              {isAddEditUsers && (
                <OutlineIconButton
                  className="!bg-white"
                  icon={<UserReactivateIcon width="16" height="16" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestoreUserClick(rowValue.userId);
                  }}
                  aria-label="Reactivate user"
                  title="Reactivate user"
                />
              )}
            </div>
          );
        },
      },
    ],
    [handleRestoreUserClick, isAddEditUsers],
  );

  const table = useReactTable({
    data: deactivatedUsersList || [],
    columns,
    getCoreRowModel: getCoreRowModel(),

    state: {
      pagination: deactivatedUsersPagination,
    },

    //** START PAGINATION */
    rowCount: deactivatedUsersRowCount,
    onPaginationChange: (updaterOrValue) => {
      if (typeof updaterOrValue === 'function') {
        const newPaginationState = updaterOrValue(deactivatedUsersPagination);
        dispatch(setDeactivatedUsersPagination(newPaginationState));
      } else {
        dispatch(setDeactivatedUsersPagination(updaterOrValue));
      }
    },
    manualPagination: true,
    //** END PAGINATION */
  });

  return {
    table,
    isTableDataLoading: getDeactivatedUsersFetching,
  };
};
